import request from '@/utils/request';
import { authStorage } from '@/utils/storage/authStorage';
import { TIME } from '@/config/constants';
import UserStore from '@/store/UserStore';
import { getRoleRoutes } from './role';
import { getRoutesByPermission } from '@/permission/getRoutesByPermission';
import axios from 'axios';

export interface LoginParams {
  account: string;        // Username or email
  password: string;
  phone?: string;
  //   verificationCode?: string;
  code: string;           // Captcha or SMS code
  totp_code?: string;     // 2FA
  //   qrToken?: string;
  //   loginType: 'account' | 'mobile' | 'qrcode';
  remember?: boolean;     // Used only on frontend for token storage duration
}

export interface UserInfo {
  id?: string;
  username?: string;
  email?: string;
  roles?: string[];
  inviteCode: string;
  invitePage: string;
  rewardRatio?: number;
  spotRatio?: number;
  permissions: Record<string, boolean>;
  dynamicRoutesList: string[];
  avatar?: string;
  status?: string;
  lastLogin?: string;
}

export interface LoginResponse {
  user_id: string;
  account: string;
  email?: string;
  avatar?: string;
  invite_code: string;
  invite_page: string;
  token: string;
  model_id?: string;
  reward_ratio?: number;
  spot_ratio?: number;
  rake_back_time?: number;
  is_unsafe?: boolean;
  permission: Record<string, boolean>;
  dynamicRoutesList: string[];

  code: number;
  msg?: string;
}

// export interface LoginResponse {
//   token: string;
//   refreshToken: string;
//   expiresIn: number;
//   user: UserInfo;
// }

export interface QRCodeResponse {
  qrUrl: string;
  qrToken: string;
}

export interface RegisterParams {
  account: string;      // email
  invite_code: string;  // invite_parent
  code: string;         // verificationCode
  password: string;     // encrpyt
}

interface ApiResponse<T> {
  code: number;
  data: T;
  message: string;
}

interface VerificationCodeData {
  verifyCode: string;
}

interface ResetPasswordParams {
  mobile: string;
  verificationCode: string;
  newPassword: string;
}

interface TokenInfo {
  token: string;
  expiresIn: number;
}

class AuthService {
  private isRefreshing: boolean = false;

  async login(params: LoginParams): Promise<LoginResponse> {
    try {
      const response = await request.post<LoginResponse>('/account/login', params);
  
      if (!response?.token) {
        throw new Error('登录响应格式错误');
      }
      const dynamicRoutesList = await getRoutesByPermission(response.permission, response.model_id ? Number(response.model_id) : undefined);
      response.dynamicRoutesList = dynamicRoutesList;

      authStorage.setTokenInfo({
        token: response.token,
        refreshToken: '', // no refresh token now
        expiresIn: TIME.TOKEN_EXPIRE,
        remember: params.remember
      });
  
      return response;
    } catch (error) {
      console.error('Login failed:', error);
      throw error;
    }
  }

  async logout(): Promise<void> {
    try {
      await request.post('/account/logout');
    } finally {
      authStorage.clearAuth();
    }
  }

  async getCurrentUser(): Promise<UserInfo> {
    return await request.get<UserInfo>('/account/me');
  }

  async getCurrentUserWithToken(token: string): Promise<UserInfo> {
    // Create a custom request with the specific token
    try {
      const response = await request.get<UserInfo>('/account/me', {
        headers: {
          'Token': token
        }
      });
      
      // If the response doesn't include dynamic routes, try to get them
      if (!response.dynamicRoutesList) {
        const dynamicRoutesList = await getRoutesByPermission(response.permissions || {}, (response as any).model_id ? Number((response as any).model_id) : undefined);
        response.dynamicRoutesList = dynamicRoutesList;
      }
      
      return response;
    } catch (error) {
      console.error('Failed to get user info with token:', error);
      throw error;
    }
  }

  // Validate token without getting full user info (lighter request)
  async validateToken(token: string): Promise<boolean> {
    try {
      await this.getCurrentUserWithToken(token);
      return true;
    } catch (error) {
      console.error('Token validation failed:', error);
      return false;
    }
  }

  setTokenDirectly(token: string): void {
    authStorage.setTokenInfo({
      token: token,
      refreshToken: '', // no refresh token for external login
      expiresIn: TIME.TOKEN_EXPIRE,
      remember: true // default to remember for external login
    });
  }

  async geetestVerifyApi(result: any): Promise<boolean> {
    const response = await request.post<{identifier: boolean}>('geetest/verify', result);
    return response.identifier;
  }

  async sendVerificationCode(account: string, type: 'login' | 'register' | 'reset', result: any): Promise<ApiResponse<VerificationCodeData>> {
    const mode = type === 'login' ? 1 : type === 'register' ? 2 : 3; // Map type to mode
    return await request.post('/common/authcode/safe', { account, mode, ...result });
  }

  async getQRCode(): Promise<QRCodeResponse> {
    return await request.get<QRCodeResponse>('/account/qrcode');
  }

  async checkQRCodeStatus(qrToken: string): Promise<LoginResponse | { status: 'pending' | 'expired' }> {
    return await request.get<LoginResponse | { status: 'pending' | 'expired' }>(`/account/qrcode/check/${qrToken}`);
  }

  async changePassword(oldPassword: string, newPassword: string): Promise<void> {
    await request.post('/account/password/modify', {
      // newPassword: newPassword,
      // topt_code: '',
      oldPassword,
      newPassword
    });
  }

  getToken(): string | null {
    return authStorage.getToken() ||
           authStorage.getToken() ||
           null;
  }

  isAuthenticated(): boolean {
    const token = this.getToken();
    if (!token) return false;

    return authStorage.isTokenValid(TIME.TOKEN_REFRESH_AHEAD);
  }

  async register(params: RegisterParams): Promise<void> {
    await request.post('/account/register', params);
  }

  async resetPassword(params: ResetPasswordParams): Promise<void> {
    await request.post('/account/reset-password', params);
  }

  async updateProfile(data: Partial<UserInfo>): Promise<UserInfo> {
    return await request.put<UserInfo>('/account/profile', data);
  }

  async uploadAvatar(file: File): Promise<any> {
    const formData = new FormData();
    formData.append('avatar', file);
    const response = await request.upload<{ code: number; data: { url: string }; message: string }>('/account/upload-avatar', formData);
    return response;
  }

  async getDevices(): Promise<any[]> {
    return await request.get('/account/devices');
  }

  async revokeDevice(deviceId: string): Promise<void> {
    await request.delete(`/account/devices/${deviceId}`);
  }

  async getApiTokens(): Promise<any[]> {
    return await request.get('/account/tokens');
  }

  async createApiToken(data: any): Promise<any> {
    return await request.post('/account/tokens', data);
  }

  async revokeApiToken(tokenId: string): Promise<void> {
    await request.delete(`/account/tokens/${tokenId}`);
  }

  async updateNotificationSettings(settings: any): Promise<void> {
    await request.put('/account/notifications/settings', settings);
  }

  async affiliateLogin(token: string): Promise<LoginResponse> {
    try {
      // Create a completely separate axios instance without interceptors
      const axiosInstance = axios.create({
        baseURL: (request as any).baseURL,
        timeout: 10000,
        headers: {
          'Content-Type': 'application/json;charset=utf-8',
        }
      });
      
      // Call affiliate login with the token in the header - using the raw axios instance to bypass interceptors
      // const axiosInstance = (request as any).instance;
      
      // Generate signed body with timestamp and signature
      const signedBody = (request as any).generateSignedBody({});
      
      const response = await axiosInstance.post(
        '/account/affiliate-login',
        signedBody, // Use signed body with ts and sig
        {
          headers: {
            'Token': token
          }
        }
      );

      // Handle API response format {"ret": 1000, "msg": "", "data": {...}}
      if (response.data && typeof response.data === 'object' && 'ret' in response.data) {
        if (response.data.ret === 1000) { // 1000 is success
          // Check if we have nested data with user info
          if (response.data.data && typeof response.data.data === 'object' && 'token' in response.data.data) {
            const loginData = response.data.data;
            
            // Store the new token from response for subsequent API calls
            authStorage.setTokenInfo({
              token: loginData.token,
              refreshToken: '', // no refresh token for affiliate login
              expiresIn: TIME.TOKEN_EXPIRE,
              remember: true // default to remember for affiliate login
            });

            // Get dynamic routes based on permissions
            const dynamicRoutesList = await getRoutesByPermission(loginData.permission || {}, loginData.model_id ? Number(loginData.model_id) : undefined);
            loginData.dynamicRoutesList = dynamicRoutesList;

            return loginData as LoginResponse;
          } else {
            throw new Error('Success response but no user data found');
          }
        } else {
          // Handle error codes with redirects
          let errorMessage = response.data.msg || 'Unknown error';
          let redirectUrl = '';
          let baseURL = 'https://app.qubit.trade';
          if (window.location.href.includes('test')) {
            // baseURL = 'http://test.qb.finance';
            baseURL = 'http://test.app.qubit.trade';
          }
          if (window.location.href.includes('localhost')) {
            baseURL = 'http://localhost:3000';
          }
          switch (response.data.ret) {
            case 1110:
              errorMessage = 'Not an agent - User is not authorized as an agent';
              redirectUrl = `${baseURL}/affiliate/register`;
              break;
            case 1109:
              errorMessage = 'No valid token or not logged in as exchange user';
              redirectUrl = `${baseURL}/affiliate/register`;
              break;
            case 1003:
              errorMessage = 'Internal server error - Something wrong on server side';
              break;
            case 1106:
              errorMessage = 'Registered but not accepted - User registration pending approval';
              break;
            case 1101:
              errorMessage = 'Login prohibited - Access denied';
              redirectUrl = `${baseURL}/perpetual`;
              break;
            default:
              errorMessage = response.data.msg || `Unknown error code: ${response.data.ret}`;
          }
          
          // Create a structured error with the API response and redirect URL
          const apiError = new Error(`API Error ${response.data.ret}: ${errorMessage}`);
          (apiError as any).ret = response.data.ret;
          (apiError as any).msg = response.data.msg;
          (apiError as any).data = response.data.data;
          (apiError as any).errorMessage = errorMessage;
          (apiError as any).redirectUrl = redirectUrl; // Add redirect URL
          
          // Perform redirect if URL is specified
          if (redirectUrl) {
            window.location.replace(redirectUrl);
            return Promise.reject(apiError); // Still reject to prevent further processing
          }
          
          throw apiError;
        }
      }

      // Check if response.data has user data directly (fallback for other response formats)
      if (response.data && typeof response.data === 'object' && 'token' in response.data) {
        // This is a successful response with user data directly
        const loginData = response.data;
        
        // Store the new token from response for subsequent API calls
        authStorage.setTokenInfo({
          token: loginData.token,
          refreshToken: '', // no refresh token for affiliate login
          expiresIn: TIME.TOKEN_EXPIRE,
          remember: true // default to remember for affiliate login
        });

        // Get dynamic routes based on permissions
        const dynamicRoutesList = await getRoutesByPermission(loginData.permission || {}, loginData.model_id ? Number(loginData.model_id) : undefined);
        loginData.dynamicRoutesList = dynamicRoutesList;

        return loginData as LoginResponse;
      }

      // If we reach here, the response format is unexpected
      throw new Error('Unexpected response format');
    } catch (error) {
      // If it's our structured API error, just re-throw it
      if (error && typeof error === 'object' && 'ret' in error) {
        throw error;
      }
    
      // Handle axios errors - also add redirect logic here
      if (error && typeof error === 'object' && 'response' in error) {
        const httpError = error as any;
        
        // Check if the response data has the API error format
        const errorData = httpError.response?.data;
        if (errorData && typeof errorData === 'object' && 'ret' in errorData) {
          // Handle specific error codes with redirects
          let errorMessage = errorData.msg || 'Unknown error';
          let redirectUrl = '';
          let baseURL = 'https://app.qubit.trade';
          if (window.location.href.includes('test')) {
            // baseURL = 'http://test.qb.finance';
            baseURL = 'http://test.app.qubit.trade';
          }
          if (window.location.href.includes('localhost')) {
            baseURL = 'http://localhost:3000';
          }
          
          switch (errorData.ret) {
            case 1110:
              errorMessage = 'Not an agent - User is not authorized as an agent';
              redirectUrl = `${baseURL}/affiliate/register`;
              break;
            case 1109:
              errorMessage = 'No valid token or not logged in as exchange user';
              redirectUrl = `${baseURL}/affiliate/register`;
              break;
            case 1003:
              errorMessage = 'Internal server error - Something wrong on server side';
              break;
            case 1106:
              errorMessage = 'Registered but not accepted - User registration pending approval';
              break;
            case 1101:
              errorMessage = 'Login prohibited - Access denied';
              redirectUrl = `${baseURL}/perpetual`;
              break;
            default:
              errorMessage = errorData.msg || `Unknown error code: ${errorData.ret}`;
          }
          
          const apiError = new Error(`API Error ${errorData.ret}: ${errorMessage}`);
          (apiError as any).ret = errorData.ret;
          (apiError as any).msg = errorData.msg;
          (apiError as any).data = errorData.data;
          (apiError as any).errorMessage = errorMessage;
          (apiError as any).redirectUrl = redirectUrl;
          
          // Perform redirect if URL is specified
          if (redirectUrl) {
            window.location.replace(redirectUrl);
            return Promise.reject(apiError);
          }
          
          throw apiError;
        }
      }
    
      throw error;
    }
  }
}

export const authService = new AuthService(); 